import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { exhaustMap, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { AddDeleteRemarkStatus } from '../../../dtos/response/remarks-response/add-delete-remark-status';
import { ReservationCreationResponse } from '../../../dtos/response/reservation-creation-response/reservation-creation-response';
import { RemarksAddRemoveResponse } from '../../../dtos/response/reservation-response/remarks-add-remove-response';
import { ReservationCancelResponse } from '../../../dtos/response/reservation-response/reservation-cancel-response';
import { ReservationLookupStatus } from '../../../dtos/response/reservation-response/reservation-lookup-status';
import { GetVcrType } from '../../../models/get-vcr-type';
import { MessageKey } from '../../../models/message/message-key';
import { initializePasengerFormsState } from '../../../shared/passenger-forms/state/passenger-forms.actions';
import { setCallerName } from '../../../state/actions';
import { RootState } from '../../../state/state';
import { ReservationUseCase } from '../../../use-cases/reservation.use-case';
import { ReservationMessageConverter } from '../../../utils/message-converters/reservation/reservation-message-converter';
import { loadCardsOnFile } from '../../card-on-file-service/state/card-on-file-service.actions';
import { loadCompensation } from '../../compensation-service/state/compensation-service.actions';
import { loadEligibilityEngineRules } from '../../eligibility-engine-service/state/eligibility-engine-service.actions';
import { FeatureEligibilityStore } from '../../eligibility-engine-service/state/feature-eligibility.store';
import {
  addGlobalJetStreamMessage,
  addReservationLongMessage,
  removeReservationMessages,
  resetGlobalJetStreamMessages,
} from '../../message-service/state/message.actions';
import { clearPreOrderedMeals } from '../../preorder-meal-service/state/preorder-meal-service.actions';
import { addReservationToSearchHistory } from '../../reservation-search-history-service/state/reservation-search-history.actions';
import { getAncillaryTicketDetails, getTicketDetails, loadVcr } from '../../ticket-service/state/ticket-service.actions';
import { getSdcEligibility } from '../../sdc-eligibility-service/state/sdc-eligibility-service.actions';
import { DividePnrStore } from '../../divide/divide-pnr.store';
import { ReservationService } from '../reservation.service';
import {
  addRemark,
  addRemarkFailed,
  addRemarkSucceeded,
  cancelReservation,
  cancelReservationComplete,
  createPlaceHolderReservation,
  createPlaceHolderReservationComplete,
  createReservationWithPriceQuote,
  createReservationWithPriceQuoteComplete,
  initialLoadReservation,
  loadReservation,
  loadReservationComplete,
  loadReservationForInitialBooking,
  loadReservationWithDCSPassengerData,
  loadReservationWithDCSPassengerDataComplete,
  removeRemarks,
  removeRemarksFailed,
  removeRemarksSucceeded,
  removeReservation,
  setPassengerLoyalty,
  setTicketNumbers,
  syncPassengerLoyalty,
  syncTicketNumbers,
} from './reservation-service.actions';
import { ReservationServiceSelectors } from './reservation-service.selectors';

@Injectable()
export class ReservationServiceEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly reservationService = inject(ReservationService);
  private readonly reservationUseCase = inject(ReservationUseCase);
  private readonly reservationMessageConverter = inject(ReservationMessageConverter);
  private readonly dividePnrStore = inject(DividePnrStore);
  private readonly featureEligibilityStore = inject(FeatureEligibilityStore);

  // Dispatched after loadReservationComplete so passengers are committed to the store before filtering
  loadCompensationOnReservationLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReservationComplete),
      filter(({ reservationResponse }) => reservationResponse?.status === ReservationLookupStatus.SUCCESS),
      map(() => loadCompensation())
    )
  );

  refreshEligibilityOnReservationLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReservationComplete),
      filter(({ reservationResponse, background }) => reservationResponse?.status === ReservationLookupStatus.SUCCESS && background),
      tap(({ reservationResponse }) => {
        const confirmationCode = reservationResponse.confirmationCode;
        this.dividePnrStore.checkDivideEligibility(confirmationCode);
        this.featureEligibilityStore.checkFeatureEligibility({ confirmationCode, feature: 'ScheduleChange' });
      }),
      mergeMap(() => of(getSdcEligibility(), loadEligibilityEngineRules()))
    )
  );

  initialLoadReservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initialLoadReservation),
      withLatestFrom(
        this.store.select(ReservationServiceSelectors.getRoutedConfirmationCode),
        this.store.select(ReservationServiceSelectors.getAssociationRevalError),
        this.store.select(ReservationServiceSelectors.getPlaceholderReservation)
      ),
      exhaustMap(([action, routedConfirmationCode, associationRevalError, placeholderReservation]) => {
        this.handleInitialMessages(
          routedConfirmationCode,
          action.confirmationCode,
          action.background,
          associationRevalError,
          placeholderReservation
        );

        if (!action.background) {
          this.reservationUseCase.clearReservation();
          this.store.dispatch(removeReservation(routedConfirmationCode));
          this.store.dispatch(clearPreOrderedMeals());
        }
        this.store.dispatch(initializePasengerFormsState());

        return this.reservationService.lookup(action.confirmationCode, action.background).pipe(
          map((reservationResponse) => {
            if (reservationResponse.status === ReservationLookupStatus.SUCCESS) {
              // Add the loaded reservation to the search history
              this.store.dispatch(addReservationToSearchHistory({ reservation: reservationResponse?.reservation }));

              // Set the caller name in the store if there are passengers
              if (reservationResponse?.reservation?.passengers?.length > 0) {
                this.store.dispatch(
                  setCallerName(
                    `${reservationResponse.reservation.passengers[0].firstName} ${reservationResponse.reservation.passengers[0].lastName}`
                  )
                );
              }

              // Load the cards on file associated to any mileage plan numbers found on the reservation
              this.store.dispatch(loadCardsOnFile());

              if (reservationResponse?.reservation?.ticketNumbers?.length > 0) {
                // Grab all of the tickets including VOID tickets since the below calls will handle displays
                const ticketNumbers = reservationResponse.reservation.ticketNumbers?.map((ticketNumber) => ticketNumber.ticketNumber);
                this.store.dispatch(loadVcr(ticketNumbers, action.background, GetVcrType.RESERVATION, false));
                this.store.dispatch(getTicketDetails(ticketNumbers));
              }
              if (reservationResponse?.reservation?.ancillaryTicketNumbers?.length > 0) {
                this.store.dispatch(getAncillaryTicketDetails(reservationResponse.reservation.ancillaryTicketNumbers));
              }
            }

            return loadReservationComplete(reservationResponse, action.background);
          })
        );
      })
    )
  );

  getReservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReservation),
      exhaustMap((action) => {
        return this.reservationService.lookup(action.confirmationCode).pipe(
          map((reservationResponse) => {
            return loadReservationComplete(reservationResponse);
          })
        );
      })
    )
  );

  getReservationWithDCSPassengerData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReservationWithDCSPassengerData),
      exhaustMap((action) => {
        return this.reservationService.lookup(action.confirmationCode, true, false, true).pipe(
          map((reservationResponse) => {
            return loadReservationWithDCSPassengerDataComplete(reservationResponse);
          })
        );
      })
    )
  );

  loadReservationForInitialBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReservationForInitialBooking),
      withLatestFrom(this.store.select(ReservationServiceSelectors.getRoutedConfirmationCode)),
      exhaustMap(([action, routedConfirmationCode]) => {
        if (routedConfirmationCode !== null && routedConfirmationCode !== undefined) {
          return this.reservationService.lookup(routedConfirmationCode, true, true).pipe(
            map((reservationResponse) => {
              reservationResponse = this.reservationMessageConverter.convertInitialBookingReservationLookupMessages(reservationResponse);
              // Set the caller name in the store if there are passengers
              if (reservationResponse?.reservation?.passengers?.length > 0) {
                this.store.dispatch(
                  setCallerName(
                    `${reservationResponse.reservation.passengers[0].firstName} ${reservationResponse.reservation.passengers[0].lastName}`
                  )
                );
              }
              return loadReservationComplete(reservationResponse);
            })
          );
        }
        return of(loadReservationComplete(null));
      })
    )
  );

  cancelReservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelReservation),
      exhaustMap((action) => {
        return this.reservationService.cancelReservation(action.confirmationCode).pipe(
          map((reservationCancelResponse) => {
            this.handleCancelReservationMessages(reservationCancelResponse);
            return cancelReservationComplete(reservationCancelResponse);
          })
        );
      })
    )
  );

  syncPassengerLoyalty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncPassengerLoyalty),
      exhaustMap((action) => {
        return this.reservationService.lookup(action.confirmationCode, true).pipe(
          map((reservationResponse) => {
            const loyalty =
              reservationResponse.reservation?.passengers?.find((passenger) => passenger.hashId === action.passengerHashId)?.loyalty ??
              null;
            return setPassengerLoyalty(action.confirmationCode, action.passengerHashId, loyalty);
          })
        );
      })
    )
  );

  /**
   * When a syncTicketNumbers action is dispatched, call the reservation service, take the ticket
   * numbers from the response and dispatch a new action to update the ticket number is the store
   */
  syncTicketNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncTicketNumbers),
      exhaustMap((action) => {
        return this.reservationService.lookup(action.confirmationCode, false).pipe(
          map((reservationResponse) => {
            const ticketNumbers = reservationResponse.reservation?.ticketNumbers ?? [];
            // release the cached result from getTicketNumbers, this is so that even if the ticket numbers do no change
            // RAIN still reacts to the new result
            ReservationServiceSelectors.getTicketNumbers.release();
            return setTicketNumbers(action.confirmationCode, ticketNumbers);
          })
        );
      })
    )
  );

  /**
   * Create a placeholder reservation, stored in a separate part of the state from normal reservations
   */
  createPlaceHolderReservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createPlaceHolderReservation),
      exhaustMap((action) => {
        return this.reservationService.createFromCoupons(action.reservationFromCoupons).pipe(
          map((reservationResponse) => {
            return createPlaceHolderReservationComplete(reservationResponse);
          })
        );
      })
    )
  );

  /**
   * Create a reservation with price quote
   */
  createReservationWithPriceQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createReservationWithPriceQuote),
      exhaustMap((action) => {
        return this.reservationService.createReservationWithPriceQuote(action.createReservationWithPriceQuoteRequest).pipe(
          map((reservationResponseWrapper) => {
            reservationResponseWrapper =
              this.reservationMessageConverter.convertCreateReservationWithPriceQuoteMessages(reservationResponseWrapper);
            if (reservationResponseWrapper?.mappedMessageKeys) {
              reservationResponseWrapper.mappedMessageKeys.forEach((messageKey) => {
                this.store.dispatch(addGlobalJetStreamMessage(messageKey));
              });
            }
            return createReservationWithPriceQuoteComplete(reservationResponseWrapper);
          })
        );
      })
    )
  );

  addRemark$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addRemark),
      exhaustMap((action) => {
        return this.reservationService.addRemark(action.confirmationCode, action.addRemarkRequest).pipe(
          map((remarksAddRemoveResponse: RemarksAddRemoveResponse) => {
            if (remarksAddRemoveResponse.status === AddDeleteRemarkStatus.SUCCESS) {
              return addRemarkSucceeded(action.confirmationCode, remarksAddRemoveResponse);
            } else {
              return addRemarkFailed(remarksAddRemoveResponse.status, remarksAddRemoveResponse.errorMessage);
            }
          })
        );
      })
    )
  );

  removeRemarks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeRemarks),
      exhaustMap((action) => {
        return this.reservationService.removeRemarks(action.confirmationCode, action.remarkIds).pipe(
          map((result: RemarksAddRemoveResponse) => {
            if (result.status === AddDeleteRemarkStatus.SUCCESS) {
              return removeRemarksSucceeded(action.confirmationCode, action.remarkIds, result);
            } else {
              return removeRemarksFailed(result.status, result.errorMessage);
            }
          })
        );
      })
    )
  );

  private handleInitialMessages(
    routedConfirmationCode: string,
    actionConfirmationCode: string,
    background: boolean,
    associationRevalError: boolean,
    placeholderReservation: ReservationCreationResponse | null
  ) {
    if (!background && routedConfirmationCode !== actionConfirmationCode) {
      this.store.dispatch(removeReservationMessages());
    }
    if (associationRevalError) {
      this.store.dispatch(removeReservationMessages());
      this.store.dispatch(addReservationLongMessage(MessageKey.MANUAL_ASSOCIATION_REVAL_FAILURE));
    } else {
      if (placeholderReservation?.reservation?.confirmationCode === actionConfirmationCode) {
        this.store.dispatch(removeReservationMessages());
        this.store.dispatch(addReservationLongMessage(MessageKey.MANUAL_ASSOCIATION_SUCCESS));
      }
    }
  }

  private handleCancelReservationMessages(reservationCancelResponse: ReservationCancelResponse) {
    switch (reservationCancelResponse.content.toLowerCase()) {
      case 'no itinerary':
      case 'success':
        this.store.dispatch(resetGlobalJetStreamMessages());
        break;
      default:
        break;
    }
  }
}
